import React, { CSSProperties, useMemo } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CheckedBadge from "../../svgs/common/CheckedBadge";
import { Clock, HeadCircuit } from "@phosphor-icons/react";
import { useAppConfig } from "../../../AppConfigProvider";
import { ActivityState } from "../../../graphql/generated/graphql";

/* eslint-disable @typescript-eslint/prefer-ts-expect-error */

interface AssessmentCardProps {
  onButtonPress: () => void;
  grade: number;
  status?: "RUNNING" | "COMPLETED";
  isEvaluationPending: boolean;
  onPress: () => void;
  onHoverIn: () => void;
  onHoverOut: () => void;
  isHovered: boolean;
  maximumPoints: number;
  activityState: ActivityState | undefined;
  scheduledDate: string | Date | undefined;
}

interface AssessmentCardContentProps {
  onButtonPress: () => void;
  status: "RUNNING" | "COMPLETED";
  grade: number;
  isEvaluationPending: boolean;
  maximumPoints: number;
  isModuleLocked: boolean;
  formattedScheduledDateString: string;
}

function AssessmentCardContent({
  onButtonPress = () => {},
  status = null,
  grade,
  isEvaluationPending = false,
  maximumPoints,
  isModuleLocked,
  formattedScheduledDateString,
}: AssessmentCardContentProps) {
  const { primaryColor } = useAppConfig();
  return (
    <>
      <View style={styles.resourceItemLeft}>
        <View style={styles.textContainer}>
          <HeadCircuit fill={primaryColor} size={24} />
          <CustomText
            style={isModuleLocked && styles.cardContainerTitleLocked}
            text="Lesson Assessment"
            weight="bold"
          />
        </View>
        {isModuleLocked && (
          <View style={styles.dueDatePill}>
            <Clock size={14} color={"#344054"} style={styles.calendarIcon as CSSProperties} />
            <CustomText
              weight="bold"
              text={formattedScheduledDateString}
              size="s"
              style={styles.dueDateText}
              useTranslationText={false}
            />
          </View>
        )}
      </View>
      {!isModuleLocked &&
        (status === "COMPLETED" ? (
          <View style={styles.buttonsContainer}>
            <View style={styles.gradeContainer}>
              {!!grade && (
                <View style={styles.gradeTag}>
                  <CustomText
                    text={`${grade}/${maximumPoints}`}
                    useTranslationText={false}
                    size="s"
                    weight="ultraBold"
                    style={{ color: "#fff" }}
                  />
                </View>
              )}
              {isEvaluationPending && !grade && (
                <View style={styles.gradeTagUngraded}>
                  <CustomText
                    text="Evaluation Pending"
                    useTranslationText={false}
                    size="s"
                    weight="ultraBold"
                    style={{ color: "#fff" }}
                  />
                </View>
              )}
              {!isEvaluationPending && (
                <CustomButton
                  styleType="primary"
                  text="Retake"
                  textStyle={styles.buttonText}
                  onPress={onButtonPress}
                  style={{ backgroundColor: primaryColor }}
                />
              )}
              <CheckedBadge />
            </View>
          </View>
        ) : isEvaluationPending ? (
          <View style={styles.gradeTagUngraded}>
            <CustomText
              text="Evaluation Pending"
              useTranslationText={false}
              size="s"
              weight="ultraBold"
              style={{ color: "#fff" }}
            />
          </View>
        ) : (
          <View style={styles.buttonsContainer}>
            <CustomButton
              styleType="primary"
              text={`${status === "RUNNING" ? "Resume" : "Begin"} Assessment`}
              textStyle={styles.buttonText}
              onPress={onButtonPress}
              style={{ backgroundColor: primaryColor }}
            />
          </View>
        ))}
    </>
  );
}

function AssessmentCard({
  onButtonPress = () => {},
  status = null,
  grade,
  isEvaluationPending = false,
  onPress = () => {},
  onHoverIn = () => {},
  onHoverOut = () => {},
  isHovered = false,
  maximumPoints,
  activityState,
  scheduledDate,
}: AssessmentCardProps) {
  const isModuleLocked = useMemo(
    () => activityState === ActivityState.Scheduled && scheduledDate != null && new Date(scheduledDate) > new Date(),
    [activityState, scheduledDate]
  );

  const formattedScheduledDateString = useMemo(() => {
    if (!scheduledDate) return "Locked until further notice";
    const dueDate = new Date(scheduledDate);

    const month = dueDate.getMonth() + 1;
    const day = dueDate.getDate();

    return `Unlocks ${month}/${day}`;
  }, [scheduledDate]);

  // hide if draft
  if (activityState === ActivityState.Draft) {
    return null;
  }

  if (status !== "COMPLETED" || (status === "COMPLETED" && isEvaluationPending)) {
    return (
      <View style={styles.cardContainer}>
        <AssessmentCardContent
          onButtonPress={onButtonPress}
          status={status}
          grade={grade}
          isEvaluationPending={isEvaluationPending}
          maximumPoints={maximumPoints}
          isModuleLocked={isModuleLocked}
          formattedScheduledDateString={formattedScheduledDateString}
        />
      </View>
    );
  }

  return (
    <Pressable
      onPress={isModuleLocked ? undefined : onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      style={({ pressed }) => [
        styles.cardContainer,
        !isModuleLocked && isHovered && styles.cardContainerHovered,
        pressed && styles.cardContainerPressed,
      ]}>
      <AssessmentCardContent
        onButtonPress={onButtonPress}
        status={status}
        grade={grade}
        isEvaluationPending={isEvaluationPending}
        maximumPoints={maximumPoints}
        isModuleLocked={isModuleLocked}
        formattedScheduledDateString={formattedScheduledDateString}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  cardContainer: {
    marginVertical: 8,
    marginHorizontal: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 75,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 16,
    // @ts-expect-error - Web CSS property
    outlineWidth: 0,
    outlineColor: "transparent",
    outlineStyle: "solid",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  cardContainerTitleLocked: {
    opacity: 0.5,
  },
  resourceItemLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  textContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: 8,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: "700",
  },
  gradeContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 20,
  },
  gradeTag: {
    padding: 8,
    backgroundColor: "#0084FF",
    borderRadius: 100,
  },
  gradeTagUngraded: {
    padding: 8,
    backgroundColor: "#D0D5DD",
    borderRadius: 100,
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  cardContainerHovered: {
    // @ts-expect-error - Web CSS property
    outlineWidth: 2,
    outlineColor: "#D0D5DD",
    outlineStyle: "solid",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  cardContainerPressed: {
    backgroundColor: "#F5F5F5",
  },
  dueDatePill: {
    backgroundColor: "#EAECF0",
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 360,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  calendarIcon: {
    marginRight: 2,
  },
  dueDateText: {
    color: "#667085",
    fontSize: 12,
    fontWeight: "500",
  },
});

export default AssessmentCard;
